import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Controls, ControlsButton } from '@/components/Controls';
import DateRangeFilter from '@/components/DateRangeFilter';
import ExportButton from '@/components/ExportButton';
import FullScreenButton from '@/components/FullScreenButton';
import { PageHeading } from '@/components/Layout/Parts';
import { TableHeader } from '@/components/Table';
import Text from '@/components/Text';
import { api } from '@/utils/api';
import { CoverImagesProjectStatus } from '@/utils/enums';

import CoverImagesNewTable from './components/CoverImagesNewTable';
import CoverImagesTable from './components/CoverImagesTable';
import PostStatusFilter from './components/PostStatusFilter';
import UsersFilters from './components/UsersFilters';

const FiltersHolder = styled.div`
  display: flex;
`;

const HeadingsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CoverImages = () => {
  const [specialist, setSpecialist] = useState<number>();
  const [editor, setEditor] = useState<number>();
  const [showDrafts, setShowDrafts] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({
    rangeStart: undefined,
    rangeEnd: undefined,
  });

  const now = DateTime.now();

  const { type } = useParams();

  const fetchCsv = () =>
    api.get<never, string>(`/v1/cover-images/export-cover-exp-data`);

  const printTableBasedOnType = () => {
    switch (type) {
      case 'new':
        return <CoverImagesNewTable isDrafts={showDrafts} />;
      case 'active':
        return (
          <CoverImagesTable
            status={CoverImagesProjectStatus.PENDING}
            selectedDateRange={selectedDateRange}
            specialist={specialist}
            editor={editor}
          />
        );
      case 'done':
        return (
          <CoverImagesTable
            status={CoverImagesProjectStatus.OK}
            selectedDateRange={selectedDateRange}
            specialist={specialist}
            editor={editor}
          />
        );
      default:
        return <CoverImagesNewTable isDrafts={showDrafts} />;
    }
  };

  const { data: coverImagesUsers } = useQuery<{
    specialists: { id: number; name: string }[];
    editors: { id: number; name: string }[];
  }>(
    ['cover_images_users'],
    () =>
      api.get<
        never,
        {
          specialists: { id: number; name: string }[];
          editors: { id: number; name: string }[];
        }
      >('/v1/cover-images/cover-images-users'),
    {
      staleTime: 120 * 1000,
    },
  );

  return (
    <>
      <HeadingsRow>
        <div>
          <Text $type="caption" $color="gray">
            BORED PANDA
          </Text>
          <PageHeading>Cover Images</PageHeading>
        </div>
      </HeadingsRow>
      <TableHeader>
        <Controls>
          <NavLink
            to={`/general/cover-images/type/new`}
            children={({ isActive }) => (
              <ControlsButton active={isActive}>New</ControlsButton>
            )}
          />

          <NavLink
            to={`/general/cover-images/type/active`}
            children={({ isActive }) => (
              <ControlsButton active={isActive}>Active</ControlsButton>
            )}
          />
          <NavLink
            to={`/general/cover-images/type/done`}
            children={({ isActive }) => (
              <ControlsButton active={isActive}>Done</ControlsButton>
            )}
          />
        </Controls>
        {type === 'new' ? (
          <FiltersHolder>
            <PostStatusFilter
              isDrafts={showDrafts}
              onClick={() => setShowDrafts((prev) => !prev)}
            />
          </FiltersHolder>
        ) : (
          <FiltersHolder>
            <ExportButton
              title="Export Exp Data"
              fetchCsv={fetchCsv}
              fileName={`cover-images-exp-${now.toFormat('yyyy-LL-dd-HH_mm')}.csv`}
            />
            <DateRangeFilter
              selectedDateRange={selectedDateRange}
              setSelectedDateRange={setSelectedDateRange}
              text="Date Added"
            />
            <UsersFilters
              specialist={specialist}
              setSpecialist={setSpecialist}
              editor={editor}
              setEditor={setEditor}
              users={coverImagesUsers}
            />
            <FullScreenButton />
          </FiltersHolder>
        )}
      </TableHeader>
      {printTableBasedOnType()}
    </>
  );
};

export default CoverImages;
